import { forwardRef } from 'react';
import { TrendingDown, TrendingUp, Check, Flame } from 'lucide-react';
import type { Wrapped } from '@techsofit/api-types';
import { StatOverlayPhoto } from './StatOverlayPhoto';
import { valueLabel, metricLabel } from '../lib/goals';
import { formatDate } from '../lib/format';

/** The screenshot-friendly progress recap card (Spotify-Wrapped style). */
export const WrappedView = forwardRef<HTMLDivElement, { wrapped: Wrapped }>(function WrappedView({ wrapped: w }, ref) {
  const firstName = w.memberName.split(' ')[0] || w.memberName;
  return (
    <div ref={ref} className="mx-auto max-w-md overflow-hidden rounded-card bg-dark text-on-dark">
      {/* Hero */}
      <div className="bg-gradient-to-b from-dark to-dark-soft px-6 pb-7 pt-8 text-center">
        <p className="text-eyebrow uppercase tracking-wide text-accent">{w.gymName} · Glow Up</p>
        <h1 className="mt-1 font-display text-2xl font-semibold text-on-dark">{firstName}’s Glow Up</h1>
        <div className="mt-6">
          <p className="font-display text-5xl font-bold leading-none text-accent">{w.headline}</p>
          {w.headlineSub && <p className="mt-2 text-sm text-on-dark-muted">{w.headlineSub}</p>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-dark-line">
        <Stat value={w.totalCheckins} label={w.totalCheckins === 1 ? 'check-in' : 'check-ins'} />
        <Stat value={w.goalsReached} label={w.goalsReached === 1 ? 'goal hit' : 'goals hit'} />
        <Stat value={w.weeksTracked} label={w.weeksTracked === 1 ? 'week' : 'weeks'} />
      </div>

      {/* Before / after with the captured numbers overlaid (Strava-style) */}
      {w.beforePhotoUrl && w.afterPhotoUrl && (
        <div className="grid grid-cols-2 gap-px bg-dark-line">
          <StatOverlayPhoto url={w.beforePhotoUrl} label="Before" value={w.beforeValue} unit={w.photoUnit ?? ''} className="rounded-none" />
          <StatOverlayPhoto
            url={w.afterPhotoUrl}
            label="After"
            value={w.afterValue}
            unit={w.photoUnit ?? ''}
            delta={w.afterValue != null && w.beforeValue != null ? w.afterValue - w.beforeValue : null}
            className="rounded-none"
          />
        </div>
      )}

      {/* Goals */}
      {w.goals.length > 0 && (
        <div className="flex flex-col gap-px bg-dark-line">
          {w.goals.map((g, i) => {
            const improved = g.improved;
            return (
              <div key={i} className="flex items-center gap-3 bg-dark px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-on-dark">{g.title}</p>
                  <p className="text-xs text-on-dark-muted">
                    {metricLabel(g.metric)} · {valueLabel(g.startValue, g.unit)} → {valueLabel(g.currentValue, g.unit)}
                  </p>
                </div>
                {g.reached ? (
                  <span className="inline-flex items-center gap-1 rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink">
                    <Check className="size-3" aria-hidden /> Reached
                  </span>
                ) : g.delta != null && g.delta !== 0 ? (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${improved ? 'text-accent' : 'text-on-dark-muted'}`}>
                    {g.delta < 0 ? <TrendingDown className="size-3.5" aria-hidden /> : <TrendingUp className="size-3.5" aria-hidden />}
                    {valueLabel(Math.abs(g.delta), g.unit)}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between bg-dark px-5 py-4 text-xs text-on-dark-muted">
        <span className="inline-flex items-center gap-1">
          <Flame className="size-3.5 text-accent" aria-hidden /> {w.from ? `${formatDate(w.from)} – ${formatDate(w.to)}` : 'Just started'}
        </span>
        <span>powered by TechSoFit</span>
      </div>
    </div>
  );
});

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="bg-dark px-2 py-4 text-center">
      <p className="font-display text-2xl font-bold text-on-dark tabular">{value}</p>
      <p className="text-xs text-on-dark-muted">{label}</p>
    </div>
  );
}
